export type Task<T> = {
    execute: (signal?: AbortSignal) => Promise<T>
    resolve: (value: T | PromiseLike<T>) => void
    reject: (reason?: any) => void
    signal?: AbortSignal
}

class RequestQueue {
    private queue: Task<any>[] = []
    private isProcessing = false

    enqueue<T>(
        execute: (signal?: AbortSignal) => Promise<T>,
        signal?: AbortSignal
    ): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const task: Task<T> = { execute, resolve, reject, signal }

            if (signal?.aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
                return
            }

            const onAbort = () => {
                const index = this.queue.indexOf(task)
                if (index > -1) {
                    this.queue.splice(index, 1)
                    reject(new DOMException('Aborted', 'AbortError'))
                }
            }

            signal?.addEventListener('abort', onAbort)

            this.queue.push(task)
            this.processQueue()
        })
    }

    private async processQueue() {
        if (this.isProcessing) return
        if (this.queue.length === 0) return

        this.isProcessing = true
        const task = this.queue.shift()

        if (task) {
            if (task.signal?.aborted) {
                this.isProcessing = false
                this.processQueue()
                return
            }

            try {
                // Pass the task's signal to the execute function so it can be passed to the fetcher
                const result = await task.execute(task.signal)
                task.resolve(result)
            } catch (error) {
                task.reject(error)
            } finally {
                this.isProcessing = false
                this.processQueue()
            }
        } else {
            this.isProcessing = false
        }
    }
}

export const priceAvailabilityQueue = new RequestQueue()
